import React from 'react'
import { Box, Paper, Typography, Grid, IconButton } from '@mui/material'
import { DocumentsList } from './DocumentsList'
import { AlertsList } from './AlertsList'
import { formatDate, formatDateRangeEnd } from '../_helpers'
import { AlertCard } from './AlertCard'
import { useDashboardAlertTotals } from '../../../../queries/hooks/useDashboardAlertTotals'
import useDashboardListFilters from '../../../../util/useDashboardListFilters'
import { useDashboardFilterOptions } from '../../../../queries/hooks/useDashboardFilterOptions'
import { useDashboardTableDocuments } from '../../../../queries/hooks/useDashboardTableDocuments'
import { useResetDashboardPage } from '../_hooks'

const ClinicianDetailView: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const { data: filterOptions } = useDashboardFilterOptions()
  const { alertTotals: alertCounts, isLoading: isLoadingAlertCounts } = useDashboardAlertTotals()
  const {
    data: clinicianDocumentsData,
    isLoading: isClinicianDocumentsLoading,
    error: clinicianDocumentsError,
    currentPage: clinicianDocumentsPage,
    setCurrentPage: setClinicianDocumentsPage,
  } = useDashboardTableDocuments({
    rowsPerPage: 5,
    sortBy: 'documentAt',
    sortOrder: 'desc',
  })
  useResetDashboardPage({ filters, setCurrentPage: setClinicianDocumentsPage })
  const selectedClinician = filterOptions?.users.find(
    (user) => user.email === filters.detailViewClinicianEmail
  )
  const clinicianName = selectedClinician?.name
  const clinicianEmail = selectedClinician?.email

  const handleClose = () => {
    updateFilters({ detailViewClinicianEmail: null })
  }

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 4, position: 'relative' }}>
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
        aria-label="close"
        data-qa-id="clinician-details-close-button"
      >
        ✕
      </IconButton>

      <Typography variant="h5" gutterBottom>
        Clinician Details
      </Typography>

      {clinicianName && clinicianEmail ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6">{clinicianName}</Typography>
            <Typography variant="body1" color="text.secondary">
              {clinicianEmail}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Date range: {formatDate(filters.selectedDateRange?.from ?? '')} -{' '}
              {formatDateRangeEnd(filters.selectedDateRange?.to ?? '')}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <AlertCard severity={1} count={alertCounts.low} isLoading={isLoadingAlertCounts} />
            <AlertCard severity={2} count={alertCounts.medium} isLoading={isLoadingAlertCounts} />
            <AlertCard severity={3} count={alertCounts.critical} isLoading={isLoadingAlertCounts} />
          </Grid>

          <DocumentsList
            data={clinicianDocumentsData}
            isLoading={isClinicianDocumentsLoading}
            error={clinicianDocumentsError}
            currentPage={clinicianDocumentsPage}
            setCurrentPage={setClinicianDocumentsPage}
          />
          <AlertsList />
        </>
      ) : (
        <Typography>No data available for this clinician.</Typography>
      )}
    </Paper>
  )
}

export default ClinicianDetailView
